// ========================================
// HTTP ERROR CLASS
// Custom error class for HTTP-related errors
// ========================================

import { HTTP_ERROR_CODES, ERROR_MESSAGES, getErrorCodeFromStatus } from './httpConfig';

/**
 * Custom error class for HTTP errors
 * Provides structured error information for consistent error handling
 */
export class HttpError extends Error {
  /**
   * @param {string} message - Error message
   * @param {Object} options - Error options
   * @param {string} options.code - Error code from HTTP_ERROR_CODES
   * @param {number} options.status - HTTP status code
   * @param {*} options.data - Additional error data from response
   * @param {string} options.path - Request path
   * @param {string} options.method - HTTP method
   * @param {boolean} options.isRetryable - Whether the request can be retried
   */
  constructor(message, options = {}) {
    super(message);
    this.name = 'HttpError';
    this.code = options.code || HTTP_ERROR_CODES.UNKNOWN;
    this.status = options.status || 0;
    this.data = options.data || null;
    this.path = options.path || '';
    this.method = options.method || '';
    this.isRetryable = options.isRetryable ?? false;
    this.timestamp = new Date().toISOString();
    
    // Maintain proper stack trace
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, HttpError);
    }
  }

  /**
   * Create HttpError for unauthenticated access to protected routes
   * @param {string} path - Request path
   * @param {string} method - HTTP method
   * @returns {HttpError}
   */
  static unauthenticated(path, method) {
    return new HttpError('Authentication required', {
      code: HTTP_ERROR_CODES.UNAUTHORIZED,
      status: 401,
      path,
      method,
      isRetryable: false,
    });
  }

  /**
   * Create HttpError from a fetch Response object
   * @param {Response} response - Fetch response
   * @param {string} path - Request path
   * @param {string} method - HTTP method
   * @param {*} data - Parsed response data
   * @returns {HttpError}
   */
  static fromResponse(response, path, method, data = null) {
    const code = getErrorCodeFromStatus(response.status);
    const message = data?.message || data?.error?.message || ERROR_MESSAGES[code];
    
    return new HttpError(message, {
      code,
      status: response.status,
      data,
      path,
      method,
      isRetryable: [408, 429, 500, 502, 503, 504].includes(response.status),
    });
  }

  /**
   * Create HttpError for timeout
   * @param {string} path - Request path
   * @param {string} method - HTTP method
   * @param {number} timeoutMs - Timeout duration
   * @returns {HttpError}
   */
  static timeout(path, method, timeoutMs) {
    return new HttpError(
      `Request timed out after ${timeoutMs}ms`,
      {
        code: HTTP_ERROR_CODES.TIMEOUT,
        status: 408,
        path,
        method,
        isRetryable: true,
      }
    );
  }

  /**
   * Create HttpError for network errors
   * @param {string} path - Request path
   * @param {string} method - HTTP method
   * @param {Error} originalError - Original error
   * @returns {HttpError}
   */
  static networkError(path, method, originalError) {
    const isAborted = originalError.name === 'AbortError';
    
    return new HttpError(
      isAborted ? 'Request was cancelled' : 'Network error occurred',
      {
        code: isAborted ? HTTP_ERROR_CODES.ABORTED : HTTP_ERROR_CODES.NETWORK_ERROR,
        status: 0,
        path,
        method,
        data: { originalMessage: originalError.message },
        isRetryable: !isAborted,
      }
    );
  }

  /**
   * Get user-friendly error message
   * @returns {string}
   */
  getUserMessage() {
    return ERROR_MESSAGES[this.code] || this.message;
  }

  /**
   * Convert to plain object for logging/serialization
   * @returns {Object}
   */
  toJSON() {
    return {
      name: this.name,
      message: this.message,
      code: this.code,
      status: this.status,
      path: this.path,
      method: this.method,
      isRetryable: this.isRetryable,
      timestamp: this.timestamp,
      data: this.data,
    };
  }
}

export default HttpError;
